import React, { PureComponent } from 'react'
import T from 'prop-types'
import IT from 'react-immutable-proptypes'
import {
  ScrollBars,
  DropdownMenu,
  MenuItem,
  SaveButton
} from 'react-base'
import {
  is,
  Map,
  List
} from 'immutable'
import Table from '../table/Table'
import memoize from 'memoize-one'
import {
  TYPES
} from '../types'
import MapperFieldToken from './MapperFieldToken'
import MapperColumnHeading from './MapperColumnHeading'
import MapperRowHeading from './MapperRowHeading'
import Schema from '../Schema'
import {
  SCHEMA_SHAPE
} from '../shapes'
import {
  pluralize
} from 'pw-formatters'

class ColumnMapper extends PureComponent {
  static displayName = 'Schemer.ColumnMapper'

  static propTypes = {
    schema: SCHEMA_SHAPE,
    files: IT.mapOf(IT.list, T.string),
    initialMapping: IT.mapOf(T.number, T.string),
    onSave: T.func.isRequired,
    onCancel: T.func
  }

  static defaultProps = {
    initialMapping: Map()
  }

  state = {
    selectedFileIndex: 0, // Index of props.files that is visible in the table
    pendingMapping: this.props.initialMapping // Map of <sourceFile column index>:<schema field key>
  }

  componentDidMount() {
    if (!window.__isReactDndBackendSetUp) {
      console.error("WARNING: pw-schemer ColumnMapper requires a react-dnd backend/context to be set up in a parent component")
    }
  }

  /**
   * Given parsed input files, return a dynamic Schema for the table display.
   * Mapped columns use the original schema field definition, unmapped ones
   * use a generic definition.
   */
  static getMapperTableSchema = (firstFileRows, schema, pendingMapping) => {
    if (firstFileRows && firstFileRows.size && firstFileRows.size > 0) {
      // Add table columns matching the maximum-length parsed row
      const newSchemaDef = firstFileRows
        .maxBy(row => row.size)
        .map((val, colIndex) => {
          // If a column has a mapping, apply the schemas definition to it immediately
          return schema
            .get(pendingMapping.get(colIndex), Map())
            .withMutations(
              field => {
                let origTable = field.get('table')
                origTable = origTable && origTable instanceof Map ? origTable : Map()
                origTable = origTable.set('headerHeight', 130)
                origTable = origTable.set('stickyable', false)

                return field
                  .set('type', field.get('type', TYPES.STRING))
                  .set('display', `Column ${colIndex + 1}`)
                  .set('table', origTable)
              }
            )
        })

      return new Schema(
        newSchemaDef,
        {
          defaultColumnWidth: 300
        }
      )
    }
    return null
  }

  static getAllSchemaFields = schema => schema && schema.getCreatable()

  static getAllUnmappedSchemaFields = (schemaFields, pendingMapping) => schemaFields.filter(
    (field, fieldKey) => !pendingMapping.includes(fieldKey)
  ).sortBy(field => field.get('required') ? 0 : 1).entrySeq()

  getTableSchema = memoize(ColumnMapper.getMapperTableSchema)
  getSchemaFields = memoize(ColumnMapper.getAllSchemaFields)
  getUnmappedSchemaFields = memoize(ColumnMapper.getAllUnmappedSchemaFields)

  getColumnHeadingProps = memoize((schemaFields, pendingMapping) => Map({
    onMappingChange: this.handleMappingChange,
    mapping: pendingMapping,
    schemaFields: schemaFields
  }))

  getRowHeadingProps = memoize(
    () => Map({
      onRowChange: this.handleRowChange,
    })
  )

  getFileNamesList = memoize(files => {
    return List(files.keySeq())
  })

  getIsValid = memoize((schemaFields, pendingMapping) => schemaFields
    .filter(field => field.get('required'))
    .every((field, fieldKey) => pendingMapping.includes(fieldKey))
  )

  handleMappingChange = (schemaKey, columnKey) => {
    let newMappings = this.state.pendingMapping

    if (schemaKey) {
      newMappings = newMappings.filter(key => key !== schemaKey) // Remove any previous mapping
        .set(columnKey, schemaKey)
    }
    else {
      // Unset
      newMappings = newMappings.delete(columnKey)
    }

    this.setState({
      pendingMapping: newMappings
    })
  }

  handleRowChange = () => {

  }

  handleSaveClick = () => {
    const {
      onSave,
      files,
      schema
    } = this.props
    const {
      pendingMapping
    } = this.state

    const keysToColIndices = pendingMapping.reduce((out, fieldKey, colIdx) => out.set(fieldKey, colIdx), Map())

    const mappedOutput = files.map(
      file => file.map(
        row => keysToColIndices.map(
          (colIdx, fieldKey) => {
            const mappedVal = row.get(colIdx)
            const field = schema.get(fieldKey)
            const fieldType = field.get('type')
            if (fieldType === TYPES.ENUM) {
              // Map back to enum "ID"
              const fieldOptions = schema.getFieldOptions(field)
              const matchOption = fieldOptions.find(opt => `${opt.get('name')}`.toLowerCase() === `${mappedVal}`.toLowerCase())
              if (matchOption) {
                return matchOption.get('id')
              }
            }
            else if (fieldType === TYPES.LIST && mappedVal && mappedVal.split) {
              // Handle csv "arrays", which should be quoted comma-separated lists
              return mappedVal.split(',')
            }
            return mappedVal
          }
        )
      ).map(schema.formatInbound)
    )

    // TODO if we enable cell editing, this should pass the modified `files` instead of those provided by the parent
    onSave(mappedOutput)
  }

  handleResetClick = () => {
    this.setState({
      pendingMapping: this.props.initialMapping
    })
  }

  handleFileTabClick = fileIndex => {
    this.setState({
      selectedFileIndex: fileIndex
    })
  }

  render() {
    const  {
      schema,
      files,
      initialMapping,
      onCancel
    } = this.props
    const {
      pendingMapping,
      selectedFileIndex
    } = this.state

    const fileNamesList = this.getFileNamesList(files)
    const schemaFields = this.getSchemaFields(schema)
    const unmappedSchemaFields = this.getUnmappedSchemaFields(schemaFields, pendingMapping)
    const selectedFileName = files && fileNamesList && fileNamesList.get(selectedFileIndex)
    const firstFileRows = selectedFileName && fileNamesList && files.get(selectedFileName, List()).map(row => row.toMap())
    const basicTableSchema = this.getTableSchema(firstFileRows, schema, pendingMapping)

    const isValid = this.getIsValid(schemaFields, pendingMapping)
    const isDirty = !is(pendingMapping, initialMapping)
    // TODO when editable table is usable, allow inline tweaking of imported data, and per-row live validation

    return (
      <div className="schemer_column_mapper">
        <header>
          <p>Drag and drop individual Field Labels below to the appropriate columns. Field Labels in <span className="yellow_hint">Yellow</span> are required, others in <span className="blue_hint">Blue</span> are optional.</p>
        </header>
        <section>
          <div className="column_mapper_fields_wrap">
            <header>
              <h4>Field Labels</h4>
            </header>
            <section>
              <ScrollBars slimShady slimShadyDarker>
                {unmappedSchemaFields.map(([fieldKey, field]) => {
                  return (
                    <MapperFieldToken
                      key={fieldKey}
                      schemaKey={fieldKey}
                      schemaDef={field}
                      onMappingChange={this.handleMappingChange}
                    />
                  )
                })}
              </ScrollBars>
            </section>
          </div>
          <div className="column_mapper_table_wrap">
            <header>
              <h4>{fileNamesList.size} Imported {pluralize(fileNamesList.size, 'file')}</h4>
              <DropdownMenu
                activeMenuKeys={[selectedFileIndex]}
                buttonClasses="btn dropdown-toggle mapper_files_dropdown"
                closeOnItemClick
                allowActiveItemClick
                label={selectedFileName}
              >
                {fileNamesList.entrySeq().map(([fileIndex, fileName]) => (
                  <MenuItem
                    args={[fileIndex]}
                    key={fileIndex}
                    onClick={this.handleFileTabClick}
                  >{fileName}</MenuItem>
                ))}
              </DropdownMenu>
            </header>

            <div className="column_mapper_table">
              {basicTableSchema && firstFileRows && firstFileRows.size &&
                <Table
                  schema={basicTableSchema}
                  values={firstFileRows}
                  columnHeaderComponent={MapperColumnHeading}
                  columnHeaderComponentProps={this.getColumnHeadingProps(schemaFields, pendingMapping)}
                  renderRowHeader={false}
                  rowHeaderComponent={MapperRowHeading}
                  rowHeaderComponentProps={this.getRowHeadingProps()}
                  readOnly
                />
              }
            </div>
          </div>
        </section>
        <footer>
          {onCancel &&
            <button
              className={`reset_btn btn btn-default`}
              onClick={onCancel}
              type="button"
            >Cancel</button>
          }
          <button
            className={`reset_btn btn btn-default`}
            onClick={this.handleResetClick}
            type="button"
          >Reset</button>
          <div className="right">
            {isDirty && !isValid &&
              <div className="validation_summary_items_inline">
                <span className="icon icon-warning" />
                All required Field Labels (in yellow) must be assigned
              </div>
            }
            <div className="save_wrap">
              <SaveButton
                el="button"
                type="submit"
                className={`schemer_form_save_btn`}
                dirty={isDirty}
                disabled={!isDirty}
                invalid={!isValid}
                onSave={this.handleSaveClick}
                saveText={`Save & Close`}
                saving={false}
              />
            </div>
          </div>
        </footer>
      </div>
    )
  }
}



// export default DragDropContext(HTML5Backend)(ColumnMapper)

export default ColumnMapper